/** Runtime decoder and reviewed presenter gate for deterministic Watch recipes. */
import { z } from "zod";
import { getAuthorableWatchSignals, type WatchSignalCatalog } from "./watchSignalCatalog";
import { canonicalRatioToPercent } from "./watchRatioPresentation";

export const WATCH_RECIPE_CATALOG_SCHEMA_VERSION = 1 as const;

const IDENTIFIER = /^[a-z][a-z0-9_.-]{0,127}$/;
const LEAF_ID = /^[a-z][a-z0-9_]{0,63}$/;
const PARAMETER_ID = /^[a-z][a-z0-9_]{0,63}$/;
const DECIMAL = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;
const UNITS = [
  "USD_PER_SHARE", "PERCENT", "DECIMAL", "COUNT", "DAYS", "RATIO", "INDEX_POINTS",
] as const;
const OPERATORS = ["gt", "gte", "lt", "lte", "eq"] as const;

export type WatchRecipeUnit = typeof UNITS[number];
export type WatchRecipeOperator = typeof OPERATORS[number];

export interface WatchRecipeLeafDefinition {
  readonly leaf_id: string;
  readonly signal_id: string;
  readonly definition_version: number;
  readonly operator: WatchRecipeOperator;
  readonly unit: WatchRecipeUnit;
  readonly parameters: Readonly<Record<string, string>>;
  readonly threshold_parameter_id?: string;
  readonly threshold?: string;
  readonly recipe_parameter_id?: string;
  readonly evidence_epoch?: "LIVE_CHAIN" | "COMPLETED_REGULAR_SESSION";
  readonly customer_sentence: string;
}

export interface WatchRecipeAuthoringParameterDefinition {
  readonly parameter_id: string;
  readonly kind: "DECIMAL";
  readonly unit: "RATIO";
  readonly minimum: string;
  readonly maximum: string;
  readonly default: string;
  readonly max_decimal_places: number;
  readonly customer: {
    readonly label: string;
    readonly description: string;
    readonly display: "PERCENT";
  };
}

export interface WatchRecipeDefinition {
  readonly recipe_id: string;
  readonly definition_version: number;
  readonly schema_version?: 2;
  readonly job: "MARKET";
  readonly subject: "MARKET_RECIPE";
  readonly subject_scope: "UNDERLYING";
  readonly logic: "ALL" | "ANY";
  readonly leaves: readonly WatchRecipeLeafDefinition[];
  readonly authoring_parameters?: readonly WatchRecipeAuthoringParameterDefinition[];
  readonly evidence_time_policy:
    | "SAME_COMPLETED_REGULAR_SESSION"
    | "LIVE_CHAIN_PLUS_LATEST_COMPLETED_SESSION";
  readonly customer: {
    readonly name: string;
    readonly description: string;
    readonly summary: string;
  };
}

export interface WatchRecipeCatalog {
  readonly schema_version: typeof WATCH_RECIPE_CATALOG_SCHEMA_VERSION;
  readonly recipes: readonly WatchRecipeDefinition[];
}

export interface ReviewedWatchRecipePresenter {
  readonly kind: "TREND_CONFIRMATION" | "OPTIONS_VOLATILITY_LIQUIDITY_TREND";
  readonly name: string;
  readonly summary: string;
}

/** Resolve server-owned recipe templates without ever exposing placeholder tokens. */
export function describeWatchRecipeLeaf(
  recipe: WatchRecipeDefinition,
  leaf: WatchRecipeLeafDefinition,
  values: Readonly<Record<string, string>> = {},
): string | null {
  if (!leaf.customer_sentence.includes("{display_value}")) return leaf.customer_sentence;
  const parameterId = leaf.recipe_parameter_id;
  const definition = recipe.authoring_parameters?.find(
    (parameter) => parameter.parameter_id === parameterId,
  );
  const value = parameterId ? values[parameterId] ?? definition?.default : undefined;
  if (!definition || !value || definition.customer.display !== "PERCENT") return null;
  const displayValue = `${canonicalRatioToPercent(value)}%`;
  if (recipe.recipe_id === "market.options_volatility_liquidity_trend") {
    if (leaf.leaf_id === "atm30_iv_at_or_above_minimum") {
      return `30-day implied volatility is at least ${displayValue}.`;
    }
    if (leaf.leaf_id === "atm_median_relative_spread_at_or_below_maximum") {
      return `Typical near-the-money bid-ask spread is ${displayValue} or less.`;
    }
  }
  const resolved = leaf.customer_sentence.replaceAll("{display_value}", displayValue);
  return /\{[^}]+\}/.test(resolved) ? null : resolved;
}

const safeText = (maximum: number) => z.string().trim().min(1).max(maximum)
  .refine((value) => Array.from(value).every((character) => {
    const point = character.codePointAt(0) ?? 0;
    return point >= 32 && point !== 127;
  }));

const boundedDecimal = z.string().regex(DECIMAL).refine((value) => {
  const parsed = Number(value);
  const places = value.includes(".") ? value.length - value.indexOf(".") - 1 : 0;
  return Number.isFinite(parsed) && Math.abs(parsed) <= 1_000_000_000_000 && places <= 6;
});

const baseLeafShape = {
  leaf_id: z.string().regex(LEAF_ID),
  signal_id: z.string().regex(IDENTIFIER),
  definition_version: z.number().int().safe().positive(),
  operator: z.enum(OPERATORS),
  unit: z.enum(UNITS),
  parameters: z.record(z.string().regex(PARAMETER_ID), z.string().trim().min(1).max(64)),
  threshold_parameter_id: z.string().regex(PARAMETER_ID).optional(),
  threshold: boundedDecimal.optional(),
  customer_sentence: safeText(180),
};

const validateLegacyLeafBinding = (
  leaf: z.infer<z.ZodObject<typeof baseLeafShape>>,
  context: z.RefinementCtx,
) => {
  if ((leaf.threshold_parameter_id === undefined) !== (leaf.threshold === undefined)) {
    context.addIssue({ code: "custom", message: "Fixed threshold identity and value must be paired." });
  }
  if (leaf.threshold_parameter_id !== undefined && leaf.threshold !== undefined
    && leaf.parameters[leaf.threshold_parameter_id] !== leaf.threshold) {
    context.addIssue({ code: "custom", message: "Fixed threshold must match normalized parameters." });
  }
};

const legacyLeafSchema = z.object(baseLeafShape).strict().superRefine(validateLegacyLeafBinding);
const versionTwoLeafSchema = z.object({
  ...baseLeafShape,
  recipe_parameter_id: z.string().regex(PARAMETER_ID).optional(),
  evidence_epoch: z.enum(["LIVE_CHAIN", "COMPLETED_REGULAR_SESSION"]),
}).strict().superRefine((leaf, context) => {
  const hasBinding = leaf.threshold_parameter_id !== undefined;
  const bindingValues = Number(leaf.threshold !== undefined)
    + Number(leaf.recipe_parameter_id !== undefined);
  if ((!hasBinding && bindingValues !== 0) || (hasBinding && bindingValues !== 1)) {
    context.addIssue({ code: "custom", message: "Threshold binding must have exactly one source." });
  }
  if (leaf.threshold_parameter_id !== undefined && leaf.threshold !== undefined
    && leaf.parameters[leaf.threshold_parameter_id] !== leaf.threshold) {
    context.addIssue({ code: "custom", message: "Fixed threshold must match normalized parameters." });
  }
  if (leaf.threshold_parameter_id !== undefined && leaf.recipe_parameter_id !== undefined
    && leaf.parameters[leaf.threshold_parameter_id] !== undefined) {
    context.addIssue({ code: "custom", message: "Authored threshold cannot be pre-bound in parameters." });
  }
});

const authoringParameterSchema = z.object({
  parameter_id: z.string().regex(PARAMETER_ID),
  kind: z.literal("DECIMAL"),
  unit: z.literal("RATIO"),
  minimum: boundedDecimal,
  maximum: boundedDecimal,
  default: boundedDecimal,
  max_decimal_places: z.number().int().min(0).max(6),
  customer: z.object({
    label: safeText(80),
    description: safeText(180),
    display: z.literal("PERCENT"),
  }).strict(),
}).strict().superRefine((parameter, context) => {
  const minimum = Number(parameter.minimum);
  const maximum = Number(parameter.maximum);
  const defaultValue = Number(parameter.default);
  if (minimum > maximum || defaultValue < minimum || defaultValue > maximum) {
    context.addIssue({ code: "custom", message: "Recipe parameter bounds are inconsistent." });
  }
});

const commonRecipeShape = {
  recipe_id: z.string().regex(IDENTIFIER),
  definition_version: z.number().int().safe().positive(),
  job: z.literal("MARKET"),
  subject: z.literal("MARKET_RECIPE"),
  subject_scope: z.literal("UNDERLYING"),
  logic: z.enum(["ALL", "ANY"]),
  evidence_time_policy: z.enum([
    "SAME_COMPLETED_REGULAR_SESSION",
    "LIVE_CHAIN_PLUS_LATEST_COMPLETED_SESSION",
  ]),
  customer: z.object({
    name: safeText(80),
    description: safeText(280),
    summary: safeText(180),
  }).strict(),
};

const uniqueLeaves = <T extends { leaf_id: string; signal_id: string; definition_version: number }>(
  leaves: T[],
  context: z.RefinementCtx,
) => {
    const leafIds = leaves.map((leaf) => leaf.leaf_id);
    if (new Set(leafIds).size !== leafIds.length) {
      context.addIssue({ code: "custom", message: "Duplicate recipe leaf id." });
    }
    const identities = leaves.map((leaf) => `${leaf.signal_id}:${leaf.definition_version}`);
    if (new Set(identities).size !== identities.length) {
      context.addIssue({ code: "custom", message: "Duplicate recipe leaf identity." });
    }
};

const legacyRecipeSchema = z.object({
  ...commonRecipeShape,
  leaves: z.array(legacyLeafSchema).min(2).max(8).superRefine(uniqueLeaves),
}).strict();

const versionTwoRecipeSchema = z.object({
  ...commonRecipeShape,
  schema_version: z.literal(2),
  leaves: z.array(versionTwoLeafSchema).min(2).max(8).superRefine(uniqueLeaves),
  authoring_parameters: z.array(authoringParameterSchema).max(4)
    .superRefine((parameters, context) => {
      const ids = parameters.map((parameter) => parameter.parameter_id);
      if (new Set(ids).size !== ids.length) {
        context.addIssue({ code: "custom", message: "Duplicate recipe parameter id." });
      }
    }),
}).strict();

const recipeSchema = z.union([legacyRecipeSchema, versionTwoRecipeSchema]);

const catalogSchema = z.object({
  schema_version: z.literal(WATCH_RECIPE_CATALOG_SCHEMA_VERSION),
  recipes: z.array(recipeSchema).max(20).superRefine((recipes, context) => {
    const identities = recipes.map((recipe) => `${recipe.recipe_id}:${recipe.definition_version}`);
    if (new Set(identities).size !== identities.length) {
      context.addIssue({ code: "custom", message: "Duplicate recipe identity." });
    }
  }),
}).strict();

/** Decode the complete recipe catalog from an untrusted contracts payload. */
export function decodeWatchRecipeCatalog(value: unknown): WatchRecipeCatalog | null {
  const result = catalogSchema.safeParse(value);
  return result.success ? result.data : null;
}

const REVIEWED_TREND_CONFIRMATION = {
  recipe_id: "market.trend_confirmation",
  definition_version: 1,
  job: "MARKET",
  subject: "MARKET_RECIPE",
  subject_scope: "UNDERLYING",
  logic: "ALL",
  leaves: [
    {
      leaf_id: "close_above_sma_50",
      signal_id: "underlying.completed_regular_close.relative_to_sma",
      definition_version: 1,
      operator: "gt",
      unit: "DECIMAL",
      parameters: { direction: "ABOVE", window: "50" },
      customer_sentence: "The completed daily close is above its 50-session simple moving average.",
    },
    {
      leaf_id: "rsi_at_or_above_50",
      signal_id: "underlying.rsi.relative_to_level",
      definition_version: 1,
      operator: "gte",
      unit: "INDEX_POINTS",
      parameters: { direction: "ABOVE", level: "50" },
      threshold_parameter_id: "level",
      threshold: "50",
      customer_sentence: "Completed-daily RSI is at or above 50 index points.",
    },
  ],
  evidence_time_policy: "SAME_COMPLETED_REGULAR_SESSION",
  customer: {
    name: "Trend confirmation",
    description: "Alert when price trend and momentum confirm on the same completed regular session.",
    summary: "Price is above its 50-session average and RSI is at least 50.",
  },
} as const;

const REVIEWED_OPTIONS_VOLATILITY_LIQUIDITY_TREND = {
  recipe_id: "market.options_volatility_liquidity_trend",
  definition_version: 1,
  schema_version: 2,
  job: "MARKET",
  subject: "MARKET_RECIPE",
  subject_scope: "UNDERLYING",
  logic: "ALL",
  leaves: [
    {
      leaf_id: "atm30_iv_at_or_above_minimum",
      signal_id: "options_chain.atm_iv.standardized_30d.relative_to_level",
      definition_version: 1,
      operator: "gte",
      unit: "RATIO",
      parameters: { direction: "ABOVE" },
      threshold_parameter_id: "level",
      recipe_parameter_id: "minimum_iv",
      customer_sentence: "Standardized 30-day implied volatility is at or above {display_value}.",
      evidence_epoch: "LIVE_CHAIN",
    },
    {
      leaf_id: "atm_median_relative_spread_at_or_below_maximum",
      signal_id: "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level",
      definition_version: 1,
      operator: "lte",
      unit: "RATIO",
      parameters: { direction: "BELOW" },
      threshold_parameter_id: "level",
      recipe_parameter_id: "maximum_relative_spread",
      customer_sentence: "The coverage-qualified median relative spread is at or below {display_value}.",
      evidence_epoch: "LIVE_CHAIN",
    },
    {
      leaf_id: "close_above_sma_50",
      signal_id: "underlying.completed_regular_close.relative_to_sma",
      definition_version: 1,
      operator: "gt",
      unit: "DECIMAL",
      parameters: { direction: "ABOVE", window: "50" },
      customer_sentence: "The completed daily close is above its 50-session simple moving average.",
      evidence_epoch: "COMPLETED_REGULAR_SESSION",
    },
    {
      leaf_id: "rsi_at_or_above_50",
      signal_id: "underlying.rsi.relative_to_level",
      definition_version: 1,
      operator: "gte",
      unit: "INDEX_POINTS",
      parameters: { direction: "ABOVE", level: "50" },
      threshold_parameter_id: "level",
      threshold: "50",
      customer_sentence: "Completed-daily RSI is at or above 50 index points.",
      evidence_epoch: "COMPLETED_REGULAR_SESSION",
    },
  ],
  authoring_parameters: [
    {
      parameter_id: "minimum_iv",
      kind: "DECIMAL",
      unit: "RATIO",
      minimum: "0.01",
      maximum: "5",
      default: "0.30",
      max_decimal_places: 6,
      customer: {
        label: "Minimum implied volatility",
        description: "Choose the minimum standardized 30-day implied volatility.",
        display: "PERCENT",
      },
    },
    {
      parameter_id: "maximum_relative_spread",
      kind: "DECIMAL",
      unit: "RATIO",
      minimum: "0.001",
      maximum: "1",
      default: "0.10",
      max_decimal_places: 6,
      customer: {
        label: "Maximum relative spread",
        description: "Choose the maximum coverage-qualified median relative spread.",
        display: "PERCENT",
      },
    },
  ],
  evidence_time_policy: "LIVE_CHAIN_PLUS_LATEST_COMPLETED_SESSION",
  customer: {
    name: "Volatility, liquidity & trend",
    description: "Alert when options volatility is elevated, option spreads are within your limit, and trend and momentum confirm.",
    summary: "Implied volatility is elevated, option spreads are within your limit, price is above its 50-session average, and RSI is at least 50.",
  },
} as const;

const canonicalContractJson = (value: unknown): string => JSON.stringify(value, (_key, item) => {
  if (item === null || Array.isArray(item) || typeof item !== "object") return item;
  return Object.fromEntries(Object.entries(item as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right)));
});

/** Resolve a presenter only after the entire immutable definition is reviewed. */
export function getReviewedWatchRecipePresenter(
  definition: WatchRecipeDefinition,
): ReviewedWatchRecipePresenter | null {
  const parsedDefinition = recipeSchema.safeParse(definition);
  if (!parsedDefinition.success) return null;
  const normalized = parsedDefinition.data;
  const normalizedContract = canonicalContractJson(normalized);
  const reviewed = normalizedContract === canonicalContractJson(REVIEWED_TREND_CONFIRMATION)
    ? { kind: "TREND_CONFIRMATION" as const }
    : normalizedContract === canonicalContractJson(REVIEWED_OPTIONS_VOLATILITY_LIQUIDITY_TREND)
      ? { kind: "OPTIONS_VOLATILITY_LIQUIDITY_TREND" as const }
      : null;
  if (!reviewed) return null;
  return {
    kind: reviewed.kind,
    name: definition.customer.name,
    summary: definition.customer.summary,
  };
}

/**
 * Return only recipes whose entire immutable definition and every constituent
 * signal are independently released for customer authoring.
 */
export function getAuthorableWatchRecipes(
  recipeCatalog: WatchRecipeCatalog | null,
  signalCatalog: WatchSignalCatalog | null,
): readonly WatchRecipeDefinition[] {
  const authorableLeafIdentities = new Set(
    getAuthorableWatchSignals(signalCatalog, {
      job: "MARKET",
      subjectScope: "UNDERLYING",
    }).map((signal) => `${signal.signal_id}:${signal.definition_version}`),
  );
  return (recipeCatalog?.recipes ?? []).filter((recipe) => (
    recipe.logic === "ALL"
    && getReviewedWatchRecipePresenter(recipe) !== null
    && recipe.leaves.every((leaf) => authorableLeafIdentities.has(
      `${leaf.signal_id}:${leaf.definition_version}`,
    ))
  ));
}

/** Resolve an exact currently authorable recipe without accepting substitutions. */
export function findAuthorableWatchRecipe(
  recipeCatalog: WatchRecipeCatalog | null,
  signalCatalog: WatchSignalCatalog | null,
  recipeId: string,
  definitionVersion: number,
): WatchRecipeDefinition | null {
  return getAuthorableWatchRecipes(recipeCatalog, signalCatalog).find((recipe) => (
    recipe.recipe_id === recipeId
    && recipe.definition_version === definitionVersion
  )) ?? null;
}
